import { FusaOrganization } from "@/backend/models/fusaOrganization";
import { create } from "zustand";
import { FusaOrganizationController } from "../controllers/fusaOrganizationController";
import { immer } from "zustand/middleware/immer";
import { FusaOrganizationCreateRequest, FusaOrganizationResponse } from "@/types/fusaOrganizationTypes";
import { FusaOrganizationParams } from "@/backend/access/fusaOrganization/types";

type FusaOrganizationStore = {
  fusaOrganizations: FusaOrganization[];
  setFusaOrganizations: (fusaOrganizations: FusaOrganizationParams[]) => void;
  fetchFusaOrganizationsByEnterpriseId: (enterpriseId: string) => Promise<void>;
  reset: () => void;
  createFusaOrganization: (data: FusaOrganizationCreateRequest) => Promise<FusaOrganizationResponse>;
  updateFusaOrganization: (data: FusaOrganizationParams) => Promise<FusaOrganizationResponse>;
  deleteFusaOrganization: (id: string) => Promise<void>;
  refetchFusaOrganization: (id: string) => Promise<void>;
};

const initialState = {
  fusaOrganizations: [],
};

export const useFusaOrganizationStore = create<FusaOrganizationStore>()(immer((set, get) => ({
  ...initialState,
  reset: () => {
    set(initialState);
  },
  setFusaOrganizations: (fusaOrganizations) => {
    set({ fusaOrganizations: fusaOrganizations.map((fusaOrganization) => new FusaOrganization(fusaOrganization)) });
  },
  fetchFusaOrganizationsByEnterpriseId: async (enterpriseId: string) => {
    const response = await new FusaOrganizationController().readAllByEnterpriseId(enterpriseId);
    if (!response.errors && response.fusaOrganizations) {
      get().setFusaOrganizations(response.fusaOrganizations);
    }
  },
  createFusaOrganization: async (data: FusaOrganizationCreateRequest) => {
    const response = await new FusaOrganizationController().create(data);
    if (!response.errors && response.fusaOrganization) {
      const fusaOrganizations = get().fusaOrganizations;
      get().setFusaOrganizations([...fusaOrganizations, response.fusaOrganization]);
    }
    return response;
  },
  updateFusaOrganization: async (data: FusaOrganizationParams) => {
    const response = await new FusaOrganizationController().update(data);
    if (!response.errors && response.fusaOrganization) {
      const fusaOrganizations = get().fusaOrganizations;
      const { setFusaOrganizations } = get();
      const updatedFusaOrganizations = fusaOrganizations.map(
        (fusaOrganization) => {
          if (fusaOrganization.id === response.fusaOrganization?.id) {
            return new FusaOrganization(response.fusaOrganization!);
          }
          return fusaOrganization;
        }
      );
      setFusaOrganizations(updatedFusaOrganizations);
    }
    return response;
  },
  deleteFusaOrganization: async (id: string) => {
    const response = await new FusaOrganizationController().delete(id);
    if (!response.errors) {
      const fusaOrganizations = get().fusaOrganizations;
      get().setFusaOrganizations(fusaOrganizations.filter((fusaOrganization) => fusaOrganization.id !== id));
    }
  },
  refetchFusaOrganization: async (id: string) => {
    const response = await new FusaOrganizationController().read(id);
    if (!response.errors && response.fusaOrganization) {
      const fusaOrganizations = get().fusaOrganizations;
      get().setFusaOrganizations(fusaOrganizations.map((fo) => fo.id === id ? response.fusaOrganization! : fo));
    }
  },
})));